import { FileText, Globe, Cpu, Shield } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { EmployeeProfile } from '@/types'

interface EmployeeDescriptionProps {
  employee: EmployeeProfile
}

/**
 * Employee Description Component
 * 
 * Displays detailed description with formatting support and key information sections:
 * - Main description
 * - Use cases
 * - System requirements  
 * - Supported integrations
 * - Languages
 */
export function EmployeeDescription({ employee }: EmployeeDescriptionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-4 h-4" />
          About {employee.name}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Main Description */}
        <div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {employee.description}
          </p>
        </div>

        {/* Use Cases */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
            <Cpu className="w-4 h-4" />
            Use Cases
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {employee.useCases.map((useCase, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="text-primary mt-1">•</span>
                <span>{useCase}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* System Requirements */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            System Requirements
          </h3>
          <ul className="space-y-1">
            {employee.requirements.map((requirement, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="text-primary mt-1">•</span>
                <span>{requirement}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Supported Integrations */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-2">
            Supported Integrations
          </h3>
          <div className="flex flex-wrap gap-2">
            {employee.integrations.map((integration, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full"
              >
                {integration}
              </span>
            ))}
          </div>
        </div>

        {/* Supported Languages */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
            <Globe className="w-4 h-4" />
            Supported Languages
          </h3>
          <div className="flex flex-wrap gap-2">
            {employee.languages.slice(0, 12).map((language, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200"
              >
                {language}
              </span>
            ))}
            {employee.languages.length > 12 && (
              <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                +{employee.languages.length - 12} more
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

EmployeeDescription.displayName = "EmployeeDescription"